import { createStore } from '@orderx/lab/xr/store';
import { useApiQuery } from '@orderx/lab/xr/http';
import { OutletThemeColor } from '../XV2';
import { useEffect, useCallback } from 'react';
import { useInterval } from './interval';
import { useOnResume } from './on-resume';
import { useCurrency } from './currency';
import { ZoneLayoutType } from '../XV2';
import { useUserSession } from './user-session';
import { awsIotSubscription } from '../services/aws-iot/subscription';
import { apiPublicZonePost } from '../api/public.zone.[post]';
import { LocationConfigDataServiceFeeGroups, LocationConfig } from '../XV2';
import { XV1 } from '../XV1';

type ServiceFeeConfig = LocationConfig & {
    data: LocationConfigDataServiceFeeGroups;
};

type Store = {
    isLoadingZone: boolean;
    location: {
        id: number;
        name: string;
        isDevelopment: boolean;
        serviceFeePercent: number;
        serviceFeeAmount: number;
        isServiceFeeDisplayedInList: boolean;
    };
    serviceFeeConfig?: ServiceFeeConfig;
    response: XV1.API.PublicZonePost.Output;
};

const storeInitialState: Store = {
    isLoadingZone: false,
    location: undefined as any,
    response: undefined as any,
    serviceFeeConfig: undefined,
};

const { useStore } = createStore<Store>(storeInitialState);

export function useZone() {
    const [state, setState] = useStore();
    const { response } = state;
    const { tagIdsClaimed } = useUserSession();

    const {
        run: loadZone,
        isLoading: isLoadingZone,
        reset,
    } = useApiQuery<XV1.API.PublicZonePost.Output>(async (id) => {
        const response = await apiPublicZonePost({
            body: {
                zoneId: id,
                tagIdsClaimed,
            },
        });

        setState((state) => {
            state.response = response;
        });
    });

    return {
        ...state,

        serviceFeeConfig: response?.serviceFeeConfig,

        outlet: response?.outlet,

        zone: response?.zone,

        zoneId: response?.zone?.id,

        currency: response?.currency,

        response,

        location: response?.location,

        menuProducts: response?.menu || [],

        isLoadingZone,

        isLayoutTypeGroupedByCategory() {
            return response?.zone?.layoutType === ZoneLayoutType.GROUPED_BY_CATEGORY;
        },

        getMenu() {
            return response?.menu || [];
        },

        useRegister(zoneId: number) {
            const { setCurrency } = useCurrency();

            const updateZone = useCallback(() => {
                loadZone(zoneId);
            }, [zoneId]);

            awsIotSubscription.useOutletUpdate(response?.outlet?.id, updateZone);
            awsIotSubscription.useZoneUpdate(zoneId, updateZone);

            useEffect(updateZone, [updateZone]);
            useInterval(updateZone, 60000, [updateZone]);
            useOnResume(updateZone, [updateZone]);

            useEffect(() => {
                if (response) {
                    setCurrency(response.currency);

                    const isThemeLight = response.outlet.themeColor === OutletThemeColor.LIGHT;

                    if (isThemeLight) {
                        document.documentElement.removeAttribute('dark');

                        return () => {
                            document.documentElement.setAttribute('dark', '1');
                        };
                    }
                }
            }, [response]);

            useEffect(() => {
                return () => {
                    reset();
                    setState(storeInitialState);
                };
            }, []);
        },
    };
}
